import { Dataset, ExportOptions, ExportResult } from './types';

/**
 * Export utilities for converting datasets to various formats
 */

export function convertToCSV(
  datasets: Dataset[],
  options: ExportOptions
): string {
  const lines: string[] = [];

  if (options.includeMetadata) {
    lines.push('# DataLab Explorer Export');
    lines.push(`# Generated: ${new Date().toISOString()}`);
    lines.push(`# Datasets: ${datasets.map(d => d.name).join(', ')}`);
    lines.push('');
  }

  datasets.forEach((dataset, datasetIndex) => {
    if (datasets.length > 1) {
      lines.push(`# Dataset: ${dataset.name}`);
      if (options.includeSchema) {
        lines.push(`# Source: ${dataset.source}`);
        lines.push(`# Description: ${dataset.description}`);
        lines.push(`# Quality Score: ${dataset.qualityScore}%`);
        lines.push('');
      }
    }

    // Headers
    const headers = dataset.schema.map(field => field.name);

    lines.push(headers.map(h => `"${h}"`).join(','));

    // Sample data
    dataset.sampleData.forEach(row => {
      const values = headers.map(header => {
        const value = row[header];

        if (value === null || value === undefined) {
          return '';
        }
        if (typeof value === 'string') {
          return `"${value.replace(/"/g, '""')}"`;
        }

        return String(value);
      });

      lines.push(values.join(','));
    });

    if (datasetIndex < datasets.length - 1) {
      lines.push(''); // Separator between datasets
    }
  });

  return lines.join('\n');
}

export function convertToJSON(
  datasets: Dataset[],
  options: ExportOptions
): string {
  const exportData: any = {
    metadata: options.includeMetadata
      ? {
          exportedAt: new Date().toISOString(),
          tool: 'DataLab Explorer',
          version: '1.0',
          totalDatasets: datasets.length,
          totalRows: datasets.reduce((sum, d) => sum + d.totalRows, 0),
        }
      : undefined,
    datasets: datasets.map(dataset => ({
      id: dataset.id,
      name: dataset.name,
      description: options.includeMetadata ? dataset.description : undefined,
      source: options.includeMetadata ? dataset.source : undefined,
      category: options.includeMetadata ? dataset.category : undefined,
      qualityScore: options.includeMetadata ? dataset.qualityScore : undefined,
      lastUpdated: options.includeMetadata ? dataset.lastUpdated : undefined,
      schema: options.includeSchema ? dataset.schema : undefined,
      data: dataset.sampleData,
    })),
  };

  // Remove undefined values
  const cleanData = JSON.parse(JSON.stringify(exportData));

  return JSON.stringify(cleanData, null, 2);
}

export function convertToXML(
  datasets: Dataset[],
  options: ExportOptions
): string {
  const escapeXml = (str: string): string => {
    return str.replace(/[<>&'"]/g, char => {
      switch (char) {
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '&':
          return '&amp;';
        case "'":
          return '&apos;';
        case '"':
          return '&quot;';
        default:
          return char;
      }
    });
  };

  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';

  xml += '<datalab_export>\n';

  if (options.includeMetadata) {
    xml += '  <metadata>\n';
    xml += `    <exported_at>${new Date().toISOString()}</exported_at>\n`;
    xml += `    <tool>DataLab Explorer</tool>\n`;
    xml += `    <total_datasets>${datasets.length}</total_datasets>\n`;
    xml += `    <total_rows>${datasets.reduce((sum, d) => sum + d.totalRows, 0)}</total_rows>\n`;
    xml += '  </metadata>\n';
  }

  xml += '  <datasets>\n';

  datasets.forEach(dataset => {
    xml += '    <dataset>\n';
    xml += `      <id>${escapeXml(dataset.id)}</id>\n`;
    xml += `      <name>${escapeXml(dataset.name)}</name>\n`;

    if (options.includeMetadata) {
      xml += `      <description>${escapeXml(dataset.description)}</description>\n`;
      xml += `      <source>${escapeXml(dataset.source)}</source>\n`;
      xml += `      <category>${escapeXml(dataset.category || '')}</category>\n`;
      xml += `      <quality_score>${dataset.qualityScore}</quality_score>\n`;
    }

    if (options.includeSchema) {
      xml += '      <schema>\n';
      dataset.schema.forEach(field => {
        xml += '        <field>\n';
        xml += `          <name>${escapeXml(field.name)}</name>\n`;
        xml += `          <type>${escapeXml(field.type)}</type>\n`;
        xml += `          <nullable>${field.nullable}</nullable>\n`;
        if (field.description) {
          xml += `          <description>${escapeXml(field.description)}</description>\n`;
        }
        xml += '        </field>\n';
      });
      xml += '      </schema>\n';
    }

    xml += '      <data>\n';
    dataset.sampleData.forEach(row => {
      xml += '        <row>\n';
      Object.entries(row).forEach(([key, value]) => {
        xml += `          <${escapeXml(key)}>${escapeXml(String(value || ''))}</${escapeXml(key)}>\n`;
      });
      xml += '        </row>\n';
    });
    xml += '      </data>\n';
    xml += '    </dataset>\n';
  });

  xml += '  </datasets>\n';
  xml += '</datalab_export>';

  return xml;
}

export function convertToRScript(
  datasets: Dataset[],
  options: ExportOptions
): string {
  const lines: string[] = [];

  // Header comments
  lines.push('# DataLab Explorer - R Analysis Script');
  lines.push(`# Generated: ${new Date().toISOString()}`);
  lines.push(`# Datasets: ${datasets.map(d => d.name).join(', ')}`);
  lines.push('');

  // Load required libraries
  lines.push('# Load required libraries');
  lines.push('library(readr)     # For data reading');
  lines.push('library(dplyr)     # For data manipulation');
  lines.push('library(ggplot2)   # For data visualization');
  lines.push('library(corrplot)  # For correlation plots');
  lines.push('library(summary)   # For data summaries');
  lines.push('library(VIM)       # For missing data visualization');
  lines.push('');

  // Function to create sample data
  lines.push('# Helper function to create data frames');
  lines.push('create_sample_data <- function() {');
  lines.push('  cat("Creating sample datasets...\\n")');
  lines.push('');

  datasets.forEach((dataset, index) => {
    const varName = `dataset_${index + 1}`;

    lines.push(`  # Dataset: ${dataset.name}`);

    if (options.includeMetadata) {
      lines.push(`  # Source: ${dataset.source}`);
      lines.push(`  # Description: ${dataset.description}`);
      lines.push(`  # Quality Score: ${dataset.qualityScore}%`);
    }

    // Create data frame structure
    const columns: string[] = [];

    dataset.schema.forEach(field => {
      const sampleValues = dataset.sampleData.map(row => {
        const value = row[field.name];

        if (value === null || value === undefined) {
          return 'NA';
        }
        if (typeof value === 'string') {
          return `"${value.replace(/"/g, '\\"')}"`;
        }

        return String(value);
      });

      const rType = getRType(field.type);

      columns.push(
        `    ${field.name} = ${rType}(c(${sampleValues.join(', ')}))`
      );
    });

    lines.push(`  ${varName} <- data.frame(`);
    lines.push(columns.join(',\n'));
    lines.push('  )');
    lines.push('');

    // Add metadata as attributes if requested
    if (options.includeMetadata) {
      lines.push(`  attr(${varName}, "source") <- "${dataset.source}"`);
      lines.push(
        `  attr(${varName}, "description") <- "${dataset.description}"`
      );
      lines.push(
        `  attr(${varName}, "quality_score") <- ${dataset.qualityScore}`
      );
      lines.push('');
    }
  });

  lines.push('  # Return list of datasets');
  lines.push('  return(list(');
  datasets.forEach((dataset, index) => {
    const varName = `dataset_${index + 1}`;
    const comma = index < datasets.length - 1 ? ',' : '';

    lines.push(`    "${dataset.name}" = dataset_${index + 1}${comma}`);
  });
  lines.push('  ))');
  lines.push('}');
  lines.push('');

  // Main analysis section
  lines.push('# ============================================');
  lines.push('# MAIN ANALYSIS');
  lines.push('# ============================================');
  lines.push('');
  lines.push('# Load the datasets');
  lines.push('datasets <- create_sample_data()');
  lines.push('');

  // Data exploration for each dataset
  datasets.forEach((dataset, index) => {
    const datasetName = dataset.name;

    lines.push(`# ============================================`);
    lines.push(`# Analysis for: ${datasetName}`);
    lines.push(`# ============================================`);
    lines.push(`df <- datasets[["${datasetName}"]]`);
    lines.push('');
    lines.push('# Basic structure and summary');
    lines.push('cat("\\n=== Dataset Overview ===\\n")');
    lines.push('print(str(df))');
    lines.push('print(summary(df))');
    lines.push('');
    lines.push('# Check for missing values');
    lines.push('cat("\\n=== Missing Values ===\\n")');
    lines.push('missing_counts <- sapply(df, function(x) sum(is.na(x)))');
    lines.push('print(missing_counts[missing_counts > 0])');
    lines.push('');

    // Identify numeric columns for correlation analysis
    const numericFields = dataset.schema.filter(field =>
      ['integer', 'float', 'double', 'numeric', 'number'].includes(
        field.type.toLowerCase()
      )
    );

    if (numericFields.length > 1) {
      lines.push('# Correlation analysis for numeric variables');
      lines.push('numeric_cols <- sapply(df, is.numeric)');
      lines.push('if(sum(numeric_cols) > 1) {');
      lines.push('  cat("\\n=== Correlation Matrix ===\\n")');
      lines.push(
        '  cor_matrix <- cor(df[, numeric_cols], use = "complete.obs")'
      );
      lines.push('  print(cor_matrix)');
      lines.push('  ');
      lines.push('  # Create correlation plot');
      lines.push('  corrplot(cor_matrix, method = "color", type = "upper",');
      lines.push(
        '           order = "hclust", tl.cex = 0.8, tl.col = "black")'
      );
      lines.push('}');
      lines.push('');
    }

    // Basic visualizations
    lines.push('# Basic visualizations');
    lines.push('if(ncol(df) >= 2) {');
    lines.push('  # Get first two columns for sample plot');
    lines.push('  col1 <- names(df)[1]');
    lines.push('  col2 <- names(df)[2]');
    lines.push('  ');
    lines.push('  if(is.numeric(df[[col1]]) && is.numeric(df[[col2]])) {');
    lines.push('    # Scatter plot for numeric variables');
    lines.push(`    p <- ggplot(df, aes_string(x = col1, y = col2)) +`);
    lines.push('      geom_point(alpha = 0.6) +');
    lines.push('      geom_smooth(method = "lm", se = FALSE) +');
    lines.push(`      labs(title = "Scatter Plot: ${datasetName}",`);
    lines.push('           subtitle = paste(col1, "vs", col2)) +');
    lines.push('      theme_minimal()');
    lines.push('    print(p)');
    lines.push('  } else {');
    lines.push('    # Bar plot for categorical data');
    lines.push('    if(is.factor(df[[col1]]) || is.character(df[[col1]])) {');
    lines.push(`      p <- ggplot(df, aes_string(x = col1)) +`);
    lines.push('        geom_bar() +');
    lines.push(`        labs(title = "Distribution: ${datasetName}",`);
    lines.push('             subtitle = col1) +');
    lines.push('        theme_minimal() +');
    lines.push(
      '        theme(axis.text.x = element_text(angle = 45, hjust = 1))'
    );
    lines.push('      print(p)');
    lines.push('    }');
    lines.push('  }');
    lines.push('}');
    lines.push('');
  });

  // Multi-dataset analysis if multiple datasets
  if (datasets.length > 1) {
    lines.push('# ============================================');
    lines.push('# MULTI-DATASET ANALYSIS');
    lines.push('# ============================================');
    lines.push('');
    lines.push('# Compare dataset sizes');
    lines.push('dataset_sizes <- sapply(datasets, nrow)');
    lines.push('cat("\\n=== Dataset Sizes ===\\n")');
    lines.push('print(dataset_sizes)');
    lines.push('');
    lines.push('# Plot dataset sizes');
    lines.push('size_df <- data.frame(');
    lines.push('  Dataset = names(dataset_sizes),');
    lines.push('  Rows = as.numeric(dataset_sizes)');
    lines.push(')');
    lines.push('');
    lines.push('p_sizes <- ggplot(size_df, aes(x = Dataset, y = Rows)) +');
    lines.push('  geom_col(fill = "steelblue", alpha = 0.7) +');
    lines.push('  labs(title = "Dataset Size Comparison",');
    lines.push('       y = "Number of Rows") +');
    lines.push('  theme_minimal() +');
    lines.push('  theme(axis.text.x = element_text(angle = 45, hjust = 1))');
    lines.push('print(p_sizes)');
    lines.push('');
  }

  // Analysis tips and next steps
  lines.push('# ============================================');
  lines.push('# ANALYSIS TIPS AND NEXT STEPS');
  lines.push('# ============================================');
  lines.push('cat("\\n=== Analysis Complete ===\\n")');
  lines.push('cat("Suggested next steps:\\n")');
  lines.push('cat("1. Explore data quality and clean missing values\\n")');
  lines.push(
    'cat("2. Perform statistical tests on interesting relationships\\n")'
  );
  lines.push('cat("3. Create detailed visualizations for insights\\n")');
  lines.push('cat("4. Build predictive models if appropriate\\n")');
  lines.push('cat("5. Export results and create reports\\n")');
  lines.push('');
  lines.push('# Uncomment to save plots');
  lines.push('# ggsave("analysis_plots.pdf", width = 10, height = 8)');

  return lines.join('\n');
}

export function convertToJupyterNotebook(
  datasets: Dataset[],
  options: ExportOptions
): string {
  const notebook = {
    nbformat: 4,
    nbformat_minor: 4,
    metadata: {
      kernelspec: {
        display_name: 'Python 3',
        language: 'python',
        name: 'python3',
      },
      language_info: {
        name: 'python',
        version: '3.8.0',
        mimetype: 'text/x-python',
        codemirror_mode: {
          name: 'ipython',
          version: 3,
        },
        pygments_lexer: 'ipython3',
        nbconvert_exporter: 'python',
      },
    },
    cells: [],
  };

  // Title cell
  notebook.cells.push({
    cell_type: 'markdown',
    metadata: {},
    source: [
      '# DataLab Explorer - Data Analysis Notebook\n',
      '\n',
      `**Generated:** ${new Date().toISOString()}\n`,
      `**Datasets:** ${datasets.map(d => d.name).join(', ')}\n`,
      '\n',
      'This notebook contains data analysis code for the datasets exported from DataLab Explorer. Follow the cells sequentially to explore and analyze your data.\n',
    ],
  });

  // Import libraries cell
  notebook.cells.push({
    cell_type: 'code',
    execution_count: null,
    metadata: {},
    outputs: [],
    source: [
      '# Import required libraries\n',
      'import pandas as pd\n',
      'import numpy as np\n',
      'import matplotlib.pyplot as plt\n',
      'import seaborn as sns\n',
      'from scipy import stats\n',
      'import warnings\n',
      "warnings.filterwarnings('ignore')\n",
      '\n',
      '# Set up plotting style\n',
      "plt.style.use('seaborn-v0_8')\n",
      "sns.set_palette('husl')\n",
      "plt.rcParams['figure.figsize'] = (12, 8)\n",
      '\n',
      "print('Libraries loaded successfully!')",
    ],
  });

  // Data loading section
  notebook.cells.push({
    cell_type: 'markdown',
    metadata: {},
    source: [
      '## 📊 Data Loading\n',
      '\n',
      "In this section, we'll create DataFrames from the exported sample data. In a real analysis, you would typically load data from CSV files or databases.\n",
    ],
  });

  // Create data loading code for each dataset
  datasets.forEach((dataset, index) => {
    const varName = `df_${index + 1}`;

    // Dataset description cell
    notebook.cells.push({
      cell_type: 'markdown',
      metadata: {},
      source: [
        `### Dataset ${index + 1}: ${dataset.name}\n`,
        '\n',
        options.includeMetadata
          ? [
              `**Source:** ${dataset.source}\n`,
              `**Description:** ${dataset.description}\n`,
              `**Quality Score:** ${dataset.qualityScore}%\n`,
              `**Total Rows:** ${dataset.totalRows.toLocaleString()}\n`,
              `**Fields:** ${dataset.schema.length}\n`,
            ].join('')
          : '',
      ],
    });

    // Data loading code
    const dataDict: any = {};

    dataset.schema.forEach(field => {
      dataDict[field.name] = dataset.sampleData.map(row => row[field.name]);
    });

    notebook.cells.push({
      cell_type: 'code',
      execution_count: null,
      metadata: {},
      outputs: [],
      source: [
        `# Create DataFrame for ${dataset.name}\n`,
        `${varName}_data = ${JSON.stringify(dataDict, null, 2)}\n`,
        `${varName} = pd.DataFrame(${varName}_data)\n`,
        '\n',
        '# Display basic information\n',
        `print(f"Dataset: ${dataset.name}")\n`,
        `print(f"Shape: {${varName}.shape}")\n`,
        `print(f"Columns: {list(${varName}.columns)}")\n`,
        `${varName}.head()`,
      ],
    });
  });

  // Data exploration section
  notebook.cells.push({
    cell_type: 'markdown',
    metadata: {},
    source: [
      '## 🔍 Data Exploration\n',
      '\n',
      "Let's explore each dataset to understand the data structure, quality, and patterns.\n",
    ],
  });

  datasets.forEach((dataset, index) => {
    const varName = `df_${index + 1}`;

    notebook.cells.push({
      cell_type: 'markdown',
      metadata: {},
      source: [`### Exploring ${dataset.name}\n`],
    });

    // Basic statistics
    notebook.cells.push({
      cell_type: 'code',
      execution_count: null,
      metadata: {},
      outputs: [],
      source: [
        `# Basic statistics for ${dataset.name}\n`,
        `print("=== Dataset Info ===")\n`,
        `${varName}.info()\n`,
        `print("\\n=== Statistical Summary ===")\n`,
        `${varName}.describe(include='all')\n`,
      ],
    });

    // Missing values analysis
    notebook.cells.push({
      cell_type: 'code',
      execution_count: null,
      metadata: {},
      outputs: [],
      source: [
        `# Missing values analysis\n`,
        `missing_values = ${varName}.isnull().sum()\n`,
        `missing_percent = (missing_values / len(${varName})) * 100\n`,
        `missing_df = pd.DataFrame({\n`,
        `    'Missing Count': missing_values,\n`,
        `    'Missing Percentage': missing_percent\n`,
        `})\n`,
        `missing_df = missing_df[missing_df['Missing Count'] > 0].sort_values('Missing Count', ascending=False)\n`,
        `\n`,
        `if not missing_df.empty:\n`,
        `    print("Missing Values Summary:")\n`,
        `    print(missing_df)\n`,
        `    \n`,
        `    # Plot missing values\n`,
        `    plt.figure(figsize=(10, 6))\n`,
        `    missing_df['Missing Percentage'].plot(kind='bar')\n`,
        `    plt.title(f'Missing Values in ${dataset.name}')\n`,
        `    plt.ylabel('Percentage Missing')\n`,
        `    plt.xticks(rotation=45)\n`,
        `    plt.tight_layout()\n`,
        `    plt.show()\n`,
        `else:\n`,
        `    print("No missing values found!")`,
      ],
    });

    // Data visualization
    const numericFields = dataset.schema.filter(field =>
      ['integer', 'float', 'double', 'numeric', 'number'].includes(
        field.type.toLowerCase()
      )
    );

    if (numericFields.length > 0) {
      notebook.cells.push({
        cell_type: 'code',
        execution_count: null,
        metadata: {},
        outputs: [],
        source: [
          `# Visualizations for ${dataset.name}\n`,
          `numeric_columns = ${varName}.select_dtypes(include=[np.number]).columns\n`,
          `\n`,
          `if len(numeric_columns) > 0:\n`,
          `    # Distribution plots\n`,
          `    fig, axes = plt.subplots(2, 2, figsize=(15, 12))\n`,
          `    fig.suptitle(f'Data Distributions - ${dataset.name}', fontsize=16)\n`,
          `    \n`,
          `    for i, col in enumerate(numeric_columns[:4]):\n`,
          `        row, col_idx = divmod(i, 2)\n`,
          `        ${varName}[col].hist(bins=20, ax=axes[row, col_idx], alpha=0.7)\n`,
          `        axes[row, col_idx].set_title(f'Distribution of {col}')\n`,
          `        axes[row, col_idx].set_xlabel(col)\n`,
          `        axes[row, col_idx].set_ylabel('Frequency')\n`,
          `    \n`,
          `    # Hide empty subplots\n`,
          `    for i in range(len(numeric_columns), 4):\n`,
          `        row, col_idx = divmod(i, 2)\n`,
          `        axes[row, col_idx].set_visible(False)\n`,
          `    \n`,
          `    plt.tight_layout()\n`,
          `    plt.show()\n`,
          `\n`,
          `    # Correlation matrix\n`,
          `    if len(numeric_columns) > 1:\n`,
          `        plt.figure(figsize=(10, 8))\n`,
          `        correlation_matrix = ${varName}[numeric_columns].corr()\n`,
          `        sns.heatmap(correlation_matrix, annot=True, cmap='coolwarm', center=0,\n`,
          `                   square=True, linewidths=0.5)\n`,
          `        plt.title(f'Correlation Matrix - ${dataset.name}')\n`,
          `        plt.tight_layout()\n`,
          `        plt.show()\n`,
          `else:\n`,
          `    print("No numeric columns found for visualization")`,
        ],
      });
    }
  });

  // Multi-dataset analysis
  if (datasets.length > 1) {
    notebook.cells.push({
      cell_type: 'markdown',
      metadata: {},
      source: [
        '## 🔗 Multi-Dataset Analysis\n',
        '\n',
        'Comparing and analyzing relationships between multiple datasets.\n',
      ],
    });

    notebook.cells.push({
      cell_type: 'code',
      execution_count: null,
      metadata: {},
      outputs: [],
      source: [
        '# Compare dataset sizes and structures\n',
        'datasets_info = []\n',
        '\n',
        ...datasets.map((dataset, index) => {
          const varName = `df_${index + 1}`;

          return [
            `datasets_info.append({\n`,
            `    'Name': '${dataset.name}',\n`,
            `    'Rows': len(${varName}),\n`,
            `    'Columns': len(${varName}.columns),\n`,
            `    'Memory_Usage_MB': ${varName}.memory_usage(deep=True).sum() / 1024**2\n`,
            `})\n`,
          ].join('');
        }),
        '\n',
        'comparison_df = pd.DataFrame(datasets_info)\n',
        'print("Dataset Comparison:")\n',
        'print(comparison_df)\n',
        '\n',
        '# Plot comparison\n',
        'fig, axes = plt.subplots(1, 2, figsize=(15, 5))\n',
        '\n',
        '# Rows comparison\n',
        "comparison_df.plot(x='Name', y='Rows', kind='bar', ax=axes[0], color='skyblue')\n",
        "axes[0].set_title('Dataset Size Comparison (Rows)')\n",
        "axes[0].set_ylabel('Number of Rows')\n",
        "axes[0].tick_params(axis='x', rotation=45)\n",
        '\n',
        '# Columns comparison\n',
        "comparison_df.plot(x='Name', y='Columns', kind='bar', ax=axes[1], color='lightcoral')\n",
        "axes[1].set_title('Dataset Complexity Comparison (Columns)')\n",
        "axes[1].set_ylabel('Number of Columns')\n",
        "axes[1].tick_params(axis='x', rotation=45)\n",
        '\n',
        'plt.tight_layout()\n',
        'plt.show()',
      ],
    });
  }

  // Analysis conclusions and next steps
  notebook.cells.push({
    cell_type: 'markdown',
    metadata: {},
    source: [
      '## 📈 Analysis Summary & Next Steps\n',
      '\n',
      '### Key Findings\n',
      '- Review the data distributions and correlations above\n',
      '- Identify any data quality issues or missing values\n',
      '- Note interesting patterns or relationships\n',
      '\n',
      '### Recommended Next Steps\n',
      '1. **Data Cleaning**: Address missing values and outliers\n',
      '2. **Feature Engineering**: Create new variables if needed\n',
      '3. **Statistical Analysis**: Perform hypothesis tests\n',
      '4. **Machine Learning**: Build predictive models if appropriate\n',
      '5. **Reporting**: Create final visualizations and insights\n',
      '\n',
      '### Additional Resources\n',
      '- [Pandas Documentation](https://pandas.pydata.org/docs/)\n',
      '- [Seaborn Gallery](https://seaborn.pydata.org/examples/index.html)\n',
      '- [Matplotlib Tutorials](https://matplotlib.org/stable/tutorials/index.html)\n',
    ],
  });

  // Save datasets cell
  notebook.cells.push({
    cell_type: 'code',
    execution_count: null,
    metadata: {},
    outputs: [],
    source: [
      '# Save processed datasets to CSV files\n',
      '# Uncomment the lines below to save your data\n',
      '\n',
      ...datasets.map((dataset, index) => {
        const varName = `df_${index + 1}`;

        return `# ${varName}.to_csv('${dataset.name.toLowerCase().replace(/[^a-z0-9]/g, '_')}.csv', index=False)\n`;
      }),
      '\n',
      "print('Analysis notebook complete!')\n",
      "print('Uncomment the save lines above to export your processed data.')",
    ],
  });

  return JSON.stringify(notebook, null, 2);
}

function getRType(fieldType: string): string {
  const type = fieldType.toLowerCase();

  if (['integer', 'int', 'long'].includes(type)) {
    return 'as.integer';
  }
  if (['float', 'double', 'numeric', 'number'].includes(type)) {
    return 'as.numeric';
  }
  if (['boolean', 'bool'].includes(type)) {
    return 'as.logical';
  }
  if (['date', 'datetime', 'timestamp'].includes(type)) {
    return 'as.Date';
  }

  return 'as.character'; // Default to character for strings and unknown types
}

export function generateFileName(format: string, datasets: Dataset[]): string {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '_');
  const datasetNames =
    datasets.length === 1
      ? datasets[0].name
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '_')
          .slice(0, 20)
      : `${datasets.length}_datasets`;

  const extensions: Record<string, string> = {
    csv: 'csv',
    json: 'json',
    xml: 'xml',
    parquet: 'parquet',
    excel: 'xlsx',
    sqlite: 'db',
    jupyter: 'ipynb',
    'r-script': 'R',
  };

  const extension = extensions[format] || format;

  return `datalab_${datasetNames}_${timestamp}.${extension}`;
}

export function estimateFileSize(datasets: Dataset[], format: string): number {
  const totalRows = datasets.reduce((sum, d) => sum + d.totalRows, 0);
  const totalFields = datasets.reduce((sum, d) => sum + d.schema.length, 0);

  // Rough size estimation based on format
  const baseSize = totalRows * totalFields * 20; // Average 20 bytes per field

  switch (format) {
    case 'csv':
      return baseSize * 0.8; // CSV is quite compact
    case 'json':
      return baseSize * 2.5; // JSON has overhead
    case 'xml':
      return baseSize * 4; // XML is verbose
    case 'parquet':
      return baseSize * 0.4; // Parquet is highly compressed
    case 'excel':
      return baseSize * 1.5; // Excel has some overhead
    case 'sqlite':
      return baseSize * 1.2; // SQLite is efficient
    case 'jupyter':
      return baseSize * 3; // Jupyter notebooks have significant metadata
    case 'r-script':
      return baseSize * 2; // R scripts include code and comments
    default:
      return baseSize;
  }
}

export function createDownloadLink(
  content: string,
  filename: string,
  mimeType: string
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL object
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

export function getMimeType(format: string): string {
  switch (format) {
    case 'csv':
      return 'text/csv';
    case 'json':
      return 'application/json';
    case 'xml':
      return 'application/xml';
    case 'excel':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'sqlite':
      return 'application/x-sqlite3';
    case 'parquet':
      return 'application/octet-stream';
    case 'jupyter':
      return 'application/x-ipynb+json';
    case 'r-script':
      return 'text/x-r';
    default:
      return 'text/plain';
  }
}
